/**
 * React Query Client Configuration
 *
 * This is the single source of truth for React Query configuration.
 * All data fetching should go through hooks that use this client.
 *
 * @see /docs/ARCHITECTURE_CONTRACT.md
 */

import { QueryClient } from "@tanstack/react-query";

/**
 * Global QueryClient instance
 *
 * Configuration rationale:
 * - staleTime: 30s - Data is considered fresh for 30 seconds, reducing refetches
 * - gcTime: 5min - Unused data is garbage collected after 5 minutes
 * - retry: 1 - One retry on failure, then show error (fail fast)
 * - refetchOnWindowFocus: true - Refetch when user returns to tab (keep data fresh)
 * - refetchOnReconnect: true - Refetch when network reconnects
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
    },
    mutations: {
      retry: 0, // Don't retry mutations by default
      // Log mutation errors to console in development
      onError: (error) => {
        if (import.meta.env.DEV) {
          console.error("[Mutation Error]", error);
        }
      },
    },
  },
});

/**
 * Invalidate all queries for a specific domain
 *
 * Usage:
 * ```ts
 * invalidateDomain('donors'); // Invalidates all donor-related queries
 * ```
 */
export function invalidateDomain(domain: string): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [domain] });
}

/**
 * Invalidate queries for a specific client within a domain
 *
 * Usage:
 * ```ts
 * invalidateClientDomain('donors', 'client-123');
 * ```
 */
export function invalidateClientDomain(
  domain: string,
  clientId: string,
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: [domain, clientId] });
}

/**
 * Clear all cached data (use sparingly, e.g., on logout)
 */
export function clearQueryCache(): void {
  queryClient.clear();
}
